import { motion } from "framer-motion";
import {
  Fingerprint, ShieldCheck, FileSearch, HardDrive, Database, Smartphone,
  Mail, Cpu, Activity, Layers, FolderSearch, Usb, ExternalLink,
} from "lucide-react";
import { Overline } from "../Reveal";

const services = [
  { icon: Layers, text: "Procesamiento de datos de evidencia: cadena de custodia." },
  { icon: FileSearch, text: "Análisis de sistemas de archivos de imágenes Windows, Linux y MAC." },
  { icon: FolderSearch, text: "Análisis de los registros de Windows, Linux y MAC." },
  { icon: Usb, text: "Análisis de dispositivos extraíbles." },
  { icon: Activity, text: "Monitoreo y análisis de eventos en Windows, Linux y MAC." },
  { icon: Mail, text: "Examen de navegadores, papelera de reciclaje y correo electrónico." },
  { icon: Cpu, text: "Investigación de imágenes forenses de RAM (Windows, Linux, MAC)." },
  { icon: FileSearch, text: "Triage de ofimática (Microsoft Office, OpenOffice, LibreOffice, PDF...)." },
  { icon: Smartphone, text: "Análisis forense de dispositivos móviles." },
  { icon: Database, text: "Análisis forense de bases de datos." },
];

const tools = [
  "OS Forensics", "Autopsy", "KAPE", "The Sleuth Kit", "SysTools EML Viewer",
  "WinPrefetchView", "Redline", "Volatility Framework",
];

export const Forensics = () => (
  <section
    id="forense"
    className="relative mx-auto max-w-7xl px-5 py-28 md:px-10 md:py-40"
    data-testid="forensics"
  >
    <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
      <div>
        <Overline>Análisis Forense</Overline>
        <h2 className="mt-6 max-w-2xl font-head text-4xl font-bold tracking-tight text-white md:text-5xl">
          Investigador Forense
        </h2>
      </div>
      <p className="max-w-md font-sans text-sm text-white/50">
        Recuperación de datos, análisis forense, preparación de informes periciales, testimonio ante la Corte, preservación de la cadena de custodia e investigación de incidentes de seguridad.
      </p>
    </div>

    <div className="mt-14 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
      {/* Left: functions + certification */}
      <div className="space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="rounded-2xl border border-white/10 bg-white/[0.03] p-8"
        >
          <h3 className="flex items-center gap-2 font-head text-lg font-semibold text-white">
            <Fingerprint className="h-5 w-5 text-cyber-cyan" /> Funciones principales
          </h3>
          <p className="mt-4 font-sans text-sm leading-relaxed text-white/65">
            Recopilar, procesar y analizar datos sobre posibles amenazas cibernéticas para ponerlos en el contexto de la empresa, detectar problemas e implementar soluciones específicas para el problema encontrado.
          </p>
        </motion.div>

        {/* Certification card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          data-testid="forensics-cert"
          className="glass overflow-hidden rounded-2xl p-8"
        >
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-cyber-cyan/10" style={{ border: "1px solid #00f0ff55" }}>
              <ShieldCheck className="h-6 w-6 text-cyber-cyan" />
            </span>
            <div>
              <p className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-cyber-cyan">Certificación</p>
              <h4 className="mt-1 font-head text-base font-semibold text-white">
                Investigador Forense de Hacking Informático
              </h4>
              <a
                href="https://aspen.eccouncil.org/Verify"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="verify-cert"
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-mono-tech text-[11px] font-bold uppercase tracking-widest text-white/85 transition-colors duration-300 hover:border-cyber-cyan hover:text-cyber-cyan"
              >
                Verificar certificación <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </motion.div>

        {/* Tools */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="rounded-2xl border border-white/10 bg-white/[0.03] p-8"
        >
          <h3 className="flex items-center gap-2 font-head text-sm font-semibold text-white">
            <HardDrive className="h-4 w-4 text-cyber-violet" /> Herramientas
          </h3>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="forensics-tools">
            {tools.map((t) => (
              <span key={t} className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 font-mono-tech text-[11px] text-white/70">
                {t}
              </span>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Right: services list */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8">
        <h3 className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-cyber-cyan">Servicios</h3>
        <div className="mt-5 space-y-1">
          {services.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
              className="group flex items-start gap-4 border-b border-white/5 py-3.5 transition-colors duration-300 hover:border-cyber-cyan/40"
              data-testid={`forensics-service-${i}`}
            >
              <s.icon className="mt-0.5 h-4 w-4 shrink-0 text-cyber-cyan/80 transition-colors group-hover:text-cyber-cyan" strokeWidth={1.6} />
              <span className="font-sans text-sm leading-relaxed text-white/70">{s.text}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
